'use client'

import { useMemo } from 'react'

import type { ParsedSymbol } from '@/features/chart/lib/configs/options-config'
import { getOptionSymbolMapForInstruments } from '@/features/chart/lib/utils/option-util'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import type { Exchange, Instrument } from '@/types/generated/common/types'

/**
 * Returns a memoized per-exchange symbol -> ParsedSymbol lookup, built from each exchange's
 * own instrument list's structured fields (strikePrice/expirationTsMs/optionType/
 * baseCurrency). Positions/orders can span multiple exchanges, and the same symbol string
 * can mean different things on two exchanges, so the map is keyed by Exchange first — pass
 * the row's own exchange to resolveOptionSymbolForExchange(exchange, symbol, map).
 */
export function useOptionSymbolMap(): Map<Exchange, Map<string, ParsedSymbol>> {
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()
  return useOptionSymbolMapFromInstruments(multiExchangeInstrumentMap)
}

/** Derive option lookups without mounting another set of instrument query observers. */
export function useOptionSymbolMapFromInstruments(
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): Map<Exchange, Map<string, ParsedSymbol>> {
  return useMemo(() => {
    const result = new Map<Exchange, Map<string, ParsedSymbol>>()
    for (const [exchange, instrumentMap] of multiExchangeInstrumentMap) {
      result.set(exchange, getOptionSymbolMapForInstruments(instrumentMap))
    }
    return result
  }, [multiExchangeInstrumentMap])
}
